import { ImplicitPrimitive } from '@/config/implicitPrimitives';

export type Domain = 'default' | 'sea' | 'air' | 'land' | 'space';

export const DOMAINS: { value: Domain; label: string; hero?: string }[] = [
  { value: 'default', label: 'Text or Image to CAD' },
  { value: 'sea', label: 'Sea', hero: '/sea-power-hero.png' },
  { value: 'air', label: 'Air', hero: '/air-power-hero.png' },
  { value: 'land', label: 'Land', hero: '/land-vehicle-hero.png' },
  { value: 'space', label: 'Space', hero: '/space-power-hero.png' },
];

// Implicit Primitives lives as a sub-tab *under* Text-or-Image-to-CAD —
// not a top-level vehicle domain. The user picks "Text or Image to CAD"
// in the domain dropdown, then sees "Implicit Primitives" as the only
// sub-tab to the right (same chrome SUPIN uses under Air).
// Ordered for the dropdown — Base shapes grouped first (simplest to most
// elaborate), then implicit SDF shapes (math-driven curves), then lattices.
// New primitives slot into their group rather than getting appended blindly.
export const PRIMITIVE_ORDER: ImplicitPrimitive[] = [
  // 'base-box',
  // 'base-sphere',
  // 'base-cylinder',
  // 'base-pipe',
  // 'base-pipe-segment',
  // 'base-ring',
  'base-lens',
  'super-ellipsoid',
  'gyroid-sphere',
  'gyroid-genus',
  'lattice-pipe',
  'lattice-manifold',
];

// Wheels live under Land → Wheels (not under the Implicit Primitives library),
// since they're a vehicle component, not a generic kernel demo. Each entry
// pairs a primitive kind with the hero image shown in the dropdown item AND
// in the center hero when selected.
export const WHEELS: {
  value: ImplicitPrimitive;
  label: string;
  hero: string;
}[] = [
  {
    value: 'rover-wheel',
    label: 'Lunar Rover Wheel',
    hero: '/lunar-rover-wheel-hero.png',
  },
];

// Heat Exchangers live under Space → Heat Exchanger. Same pattern as WHEELS: a
// dropdown of implicit-primitive composites parented to a vehicle domain. The
// helix-heat-x primitive is LEAP71's flagship tutorial — a counterflow liquid-
// liquid heat exchanger they 3D-printed and pressure-tested.
export const HEAT_EXCHANGERS: {
  value: ImplicitPrimitive;
  label: string;
  hero: string;
}[] = [
  {
    value: 'helix-heat-x',
    label: 'Helical Bimetal Liquid-Liquid',
    hero: '/heatx-hero.png',
  },
];

export type SubTab = {
  value: string;
  label: string;
  subLabel?: string;
  disabled?: boolean;
  // Optional small hero shown to the right of the label on the regular
  // sub-tab button. Picked up by the catch-all render branch below; the
  // bespoke SUPIN / Wheels / Implicit Primitives branches already manage
  // their own hero rendering.
  hero?: string;
  // Optional uniform scale applied to the hero img on the sub-tab button
  // only. Default is 1 (no scaling). The PromptView center-page hero
  // resolver always shows the full asset and is not affected.
  heroScale?: number;
};

export const DOMAIN_SUBTABS: Record<string, SubTab[]> = {
  space: [
    {
      value: 'rocket-nozzle',
      label: 'Rocket Nozzle',
      hero: '/rocket-nozzle-tab-hero.png',
      heroScale: 0.65,
    },
    { value: 'heat-exchanger', label: 'Heat Exchanger' },
  ],
  land: [
    { value: 'spaceframe', label: 'Spaceframe Chassis' },
    { value: 'cross-kart', label: 'Cross Kart (2 Seater)' },
    { value: 'tubeframe-joint', label: 'Tubeframe Joint' },
    {
      value: 'suspension-kinematics',
      label: 'JAWS',
      subLabel: 'Suspension Kinematics',
    },
    { value: 'wheels', label: 'Wheels' },
  ],
  air: [
    { value: 'airframe', label: 'Airframe', disabled: true },
    {
      value: 'nasa-supersonic-inlet',
      label: 'NASA SUPIN',
      subLabel: 'Supersonic Inlet Designer',
    },
  ],
  sea: [{ value: 'hull', label: 'Hull', disabled: true }],
  // 'default' = the Text-or-Image-to-CAD domain. Two sibling libraries:
  // Implicit Primitives (LEAP71 ShapeKernel, fully wired) and NASA Lattice
  // Primitives (NASA Unitcell Engine, gated on the Python sidecar landing —
  // dropdown items render as `comingSoon` until then). See
  // docs/issue-nasa-unitcell-engine-integration-hell.md.
  default: [
    {
      value: 'implicit-primitives',
      label: 'Implicit Primitives',
      subLabel: 'LEAP71 ShapeKernel',
    },
    {
      value: 'nasa-lattice-primitives',
      label: 'NASA Lattice Primitives',
      subLabel: 'NASA Unitcell Engine',
    },
    // NASA Unitcell Lattice Converter — upload an STL/STEP part and convert it
    // into a lighter, 3D-printable lattice (epic #309, Plan A — PicoGK fill).
    // Backend LIVE: unitcell-chat 'lattice-fill' branch → /primitives/lattice-fill
    // (telekinesis-implicit-heavy); STEP/STP is tessellated via the cad-executor
    // /step-to-stl first. v1 fills with a gyroid — the other dropdown styles fall
    // back to gyroid until the strut/.ltcx loader lands (#309).
    {
      value: 'nasa-unitcell-converter',
      label: 'NASA Unitcell Lattice Converter',
      subLabel: 'STL/STEP → Lightweight Lattice',
    },
  ],
};

// Every domain (vehicle and text-to-cad) opens with NO sub-tab selected. The
// user has to explicitly pick one — auto-selecting a default was confusing
// when a domain had multiple sub-tabs (e.g. Land's Wheels was getting hidden
// behind an auto-active Tubeframe Joint). Generate is disabled until a
// concrete sub-tab is chosen; see vehicleParamsReady in PromptView.
export const DEFAULT_SUBTAB: Record<string, string> = {
  space: '',
  land: '',
  air: '',
  sea: '',
  default: '',
};
